import { Box, Grid } from '@mui/material';
import {
  IsFreelanceOrtApplicableSwitch,
  IsTempworkOrtApplicableSwitch,
} from 'shared/components/forms/inputs';
import { useFormContext } from 'react-hook-form';
import { FlexPoolOnlyIndicator } from 'shared/components/common/flexpool-only-indicator';
import { getHelperTexts } from 'shared/components/shift-forms/components/forms/shared/get-helper-texts/get-helper-texts';
import { getMinDate } from 'shared/utils/get-min-date';
import { FormMode } from 'shared/components/shift-forms/components/forms/shared/types';
import { useEffect, useMemo, useRef } from 'react';
import { FormCheckbox, LocationFormInput } from 'redesign';
import { useGetWorkTemplate } from 'api';
import { useGetDepartmentDetail } from 'api/hooks/department/use-get-department-detail';
import { useIsHqImpersonation, useSiteDetection } from 'shared/hooks';
import { JobFormElement } from 'shared/components/shift-forms/components/forms/shared/inputs/job-inputs';
import { DepartmentFormElement } from 'shared/components/shift-forms/components/forms/shared/inputs/department-input';
import { CompanyEmploymentTypesForProjectForm } from '@types';
import { EndDateElement } from './elements/end-date-element';
import { HourlyRateElement } from './elements/hourly-rate-element';
import { StartDateElement } from './elements/start-date-element';
import { WeeklyHoursElement } from './elements/weekly-hours-element';
import { ProjectFormTypeAll } from '../types/project-form.types';

export type ProjectFormInputsProps = {
  jobsData: { id: string; name: string; flexPoolOnly: boolean }[];
  branchOfficesWithDepartments: {
    value: string;
    label: string;
    options: {
      value: string;
      label: string;
    }[];
  }[];
  mode: FormMode;
  isDisabled: boolean;
  isProjectWithActivePlacement: boolean;
  companyEmploymentType: CompanyEmploymentTypesForProjectForm | undefined;
};

export const ProjectFormInputs = ({
  isProjectWithActivePlacement,
  branchOfficesWithDepartments,
  jobsData,
  mode,
  companyEmploymentType,
}: ProjectFormInputsProps) => {
  const { site } = useSiteDetection();
  const isHqAdmin = useIsHqImpersonation();
  const { control, watch, setValue, getValues } = useFormContext<ProjectFormTypeAll>();
  const selectedWorkTemplateId = watch('jobId');
  const isFreelanceCheckboxChecked = watch('isFreelance');
  const isTempworkCheckboxChecked = watch('isTempwork');
  const selectedDepartmentId = watch('departmentId');
  const previousDepartmentId = useRef(getValues('departmentId'));

  const { data: workTemplateData, isLoading: isWorkTemplateLoading } = useGetWorkTemplate(
    site,
    selectedWorkTemplateId,
    !!selectedWorkTemplateId
  );

  const { data: department } = useGetDepartmentDetail(
    site,
    selectedDepartmentId,
    !!selectedDepartmentId
  );
  const skipDepartmentEffect = useRef(false);

  const disableOrtApplicableSwitches = !isHqAdmin && mode == 'update';

  const { minDate } = getMinDate({ allowDateBeforeToday: true });
  const helperTexts = useMemo(() => {
    return getHelperTexts(
      workTemplateData
        ? {
            breakMinutes: workTemplateData.breakMinutes,
            hourlyRateCents: workTemplateData.freelanceJob?.hourlyRateCents,
          }
        : undefined
    );
  }, [workTemplateData]);

  const workTemplateOptions = useMemo(() => {
    if (!jobsData) {
      return [];
    }
    return jobsData.map((job) => ({
      value: job.id,
      label: (
        <FlexPoolOnlyIndicator flexPoolOnly={job.flexPoolOnly}>{job.name}</FlexPoolOnlyIndicator>
      ),
    }));
  }, [jobsData]);

  const checkboxDisabled = useMemo(() => {
    const isUpdateMode = mode === 'update';

    const freelance =
      isUpdateMode ||
      !companyEmploymentType?.offersFreelanceWork ||
      (workTemplateData ? !workTemplateData.freelanceJob : false);

    const tempwork =
      isUpdateMode ||
      !companyEmploymentType?.offersTempWork ||
      (workTemplateData ? !workTemplateData.tempWorkJob : false);

    const tempworkOrtApplicable = !isTempworkCheckboxChecked;
    const freelanceOrtApplicable = !isFreelanceCheckboxChecked;

    return {
      freelance,
      tempwork,
      tempworkOrtApplicable,
      freelanceOrtApplicable,
    };
  }, [
    mode,
    companyEmploymentType,
    workTemplateData,
    isFreelanceCheckboxChecked,
    isTempworkCheckboxChecked,
  ]);

  useEffect(() => {
    if (!workTemplateData) {
      return;
    }
    skipDepartmentEffect.current = !!workTemplateData.departmentId && !!workTemplateData.location;

    setValue('location', workTemplateData.location);
    setValue('departmentId', workTemplateData.departmentId ?? '');
    setValue('isFreelance', !!workTemplateData.freelanceJob);
    setValue('isTempwork', !!workTemplateData.tempWorkJob);
    setValue('isFreelanceOrtApplicable', !!workTemplateData.freelanceJob?.ortApplicable);
    setValue('isTempworkOrtApplicable', !!workTemplateData.tempWorkJob?.ortApplicable);
  }, [workTemplateData]);

  useEffect(() => {
    if (workTemplateData && companyEmploymentType) {
      const { offersFreelanceWork, offersTempWork } = companyEmploymentType;

      setValue('isFreelance', !!workTemplateData?.freelanceJob && offersFreelanceWork);
      setValue('isTempwork', !!workTemplateData?.tempWorkJob && offersTempWork);
    }
  }, [workTemplateData, companyEmploymentType]);

  useEffect(() => {
    if (skipDepartmentEffect.current) {
      skipDepartmentEffect.current = false;
      return;
    }
    const shouldSetDepartmentLocation =
      department &&
      department.department.location &&
      previousDepartmentId.current !== department.department.id;

    if (mode !== 'create' && shouldSetDepartmentLocation) {
      setValue('location', department.department.location);
      previousDepartmentId.current = department.department.id;
      return;
    }
    if (department?.department.location && shouldSetDepartmentLocation) {
      setValue('location', department.department.location);
      previousDepartmentId.current = department.department.id;
    }
  }, [department]);

  return (
    <Box display="flex" flexDirection="row" height="100%">
      <form aria-label="Projects form">
        <Grid container item columnSpacing={4} xs={12}>
          {mode === 'create' ? (
            <Grid item xs={6}>
              <JobFormElement options={workTemplateOptions} required={true} />
            </Grid>
          ) : null}
          <Grid item xs={6}>
            <DepartmentFormElement options={branchOfficesWithDepartments} />
          </Grid>
          <Grid item xs={12}>
            <LocationFormInput required={true} />
          </Grid>
          <Grid item xs={6}>
            <StartDateElement
              key="startDate"
              control={control}
              disabled={isProjectWithActivePlacement}
              minDate={minDate}
            />
          </Grid>
          <Grid item xs={6}>
            <EndDateElement
              key="endDate"
              control={control}
              disabled={isProjectWithActivePlacement}
              minDate={minDate}
            />
          </Grid>
          <Grid item xs={6}>
            <WeeklyHoursElement
              key="weeklyHours"
              control={control}
              disabled={isWorkTemplateLoading}
              existingHours={undefined}
            />
          </Grid>

          <Grid item xs={12}>
            <FormCheckbox
              name="isTempwork"
              label="Uitzendkracht"
              control={control}
              disabled={checkboxDisabled.tempwork}
            />
            <Grid item xs={12}>
              <Grid item xs={6}>
                <IsTempworkOrtApplicableSwitch
                  disabled={checkboxDisabled.tempworkOrtApplicable || disableOrtApplicableSwitches}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormCheckbox
              name="isFreelance"
              label="Zelfstandige"
              control={control}
              disabled={checkboxDisabled.freelance}
            />
            <Grid item xs={12}>
              <Grid item xs={6}>
                <IsFreelanceOrtApplicableSwitch
                  disabled={checkboxDisabled.freelanceOrtApplicable || disableOrtApplicableSwitches}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={6}>
            <HourlyRateElement
              key="hourlyRate"
              control={control}
              disabled={
                !isFreelanceCheckboxChecked || isWorkTemplateLoading || isProjectWithActivePlacement
              }
              required={isFreelanceCheckboxChecked}
              existingRate={workTemplateData?.freelanceJob?.hourlyRateCents}
              helperTexts={helperTexts}
            />
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
